import {
	Dialog,
	Form,
	FormItem,
	Select,
	SelectItem,
	TextInput,
	Toolbar,
	useConfirmationDialog,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import {
	ensureNotNil,
	isNil,
	isNotNil,
	resolveFormattedTextString
} from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { getClientId, setClientId } from "client/Misc/clientId";
import {
	doesNotExceedLength,
	hasMaximumEntries,
	hasMinimumEntries,
	isNotEmpty
} from "client/Validation/rules";
import { ICollectionValidatorBuilder, IValidationConfiguration } from "client/Validation/builder";
import {
	createAuthentication,
	getAuthentication,
	IntegrationFlowAuthenticationKind,
	IntegrationFlowAuthenticationSaveModel,
	IntegrationFlowAuthenticationViewModel,
	KeyedValueSaveModel,
	KeyedValueViewModel,
	StringOrSecretSaveModel,
	StringOrSecretViewModel,
	StringSecretDiscriminator,
	updateAuthentication,
	IntegrationFlowOAuth2AuthenticationViewModel,
	getNewAuthentication,
	NewIntegrationFlowAuthenticationViewModel,
	IErrorSaveIntegrationFlowAuthenticationResult,
	IntegrationFlowAuthenticationErrorKind
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { IntegrationFlowAuthenticationOptionModel } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { useOAuth2AuthenticationRequiredDialog } from "./OAuth2AuthenticationRequiredDialog";
import {
	AuthenticationFormData,
	HostFormModel,
	tryParseErrorResult
} from "../common";
import { AuthenticationSettings } from "./AuthenticationSettings";
import {
	MAX_HOST_COUNT,
	MIN_HOST_COUNT,
	RestrictionSettings
} from "./RestrictionSettings";
import { PathRedirect, ReplacementPattern } from "client/Validation/serverErrors";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import { apiKeyValidationConfiguration, defaultApiKeySettings } from "./Settings/ApiKeySettings";
import { defaultOAuth2Settings, oAuth2ValidationConfiguration } from "./Settings/OAuth2Settings";
import { clientCertificateValidationConfiguration, defaultClientCertificateSettings } from "./Settings/ClientCertificateSettings";
import { afasValidationConfiguration, defaultAfasSettings } from "./Settings/AfasSettings";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { AfasOtpDialog } from "./AfasOtpDialog";
import { IValueInputAttachable } from "client/Forms/attachable";

type AuthenticationDialogProps = {
	id?: number;
	onAfterSave: (authentication: IntegrationFlowAuthenticationOptionModel) => void;
	onClose: () => void;
};

const AuthenticationDialog = (props: AuthenticationDialogProps): ReactElement => {
	const {
		id,
		onAfterSave,
		onClose: onCloseFromProps
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(isNotNil(id));
	const [formData, setFormData] = useState<AuthenticationFormData>(() => getInitialFormData());
	const [oAuth2RedirectUrl, setOAuth2RedirectUrl] = useState<string | null>(null);

	const [oAuth2AuthenticationRequiredDialog, openOAuth2AuthenticationRequiredDialog] = useOAuth2AuthenticationRequiredDialog();
	const [confirmationDialogElement, openConfirmationDialog] = useConfirmationDialog();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<AuthenticationFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);
	const afasOtpHandler: IValueInputAttachable<OptionalModel<string>> = formHandler
		.for("afas")
		.for("oneTimePassword");

	const allowedHostsToggledHandler = useCallback(
		async (value: boolean): Promise<void> => {
			if (value) {
				return;
			}

			const result: TryResult<void> = await openConfirmationDialog({
				trackingId: "remove-domain-restriction-dialog",
				title: localize("_RemoveDomainRestrictionTitle"),
				children: localize("_RemoveDomainRestrictionBody"),
				confirmText: localize("_RemoveDomainRestriction")
			});

			if (!result.wasSuccessful) {
				formHandler
					.for("allowedHosts")
					.for("isSet")
					.reducer
					.setValue(true);

				formHandler
					.for("allowedHosts")
					.for("isSet")
					.validation
					.triggerInputFinished();
			}
		},
		[
			formHandler,
			openConfirmationDialog
		]
	);

	useEffect(
		(): (() => void) | void => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);
				try {
					let formModel: AuthenticationFormData;
					let oAuth2RedirectUrl: string | null = null;

					if (isNil(id)) {
						const newAuthenticationModel: NewIntegrationFlowAuthenticationViewModel = await getNewAuthentication(signal);

						formModel = getInitialFormData();
						oAuth2RedirectUrl = newAuthenticationModel.oAuth2RedirectUrl;
					} else {
						const authenticationModel: IntegrationFlowAuthenticationViewModel = await getAuthentication(id, signal);

						formModel = convertToFormData(authenticationModel);

						if (isOAuth2Model(authenticationModel)) {
							oAuth2RedirectUrl= authenticationModel.redirectUrl;
						}
					}

					setFormData(formModel);
					setOAuth2RedirectUrl(oAuth2RedirectUrl);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			alertManager,
			id,
			onClose
		]
	);

	const authenticationErrorHandler = useCallback(
		async (errorResult: IErrorSaveIntegrationFlowAuthenticationResult): Promise<boolean> => {
			const {
				authenticationKind,
				errorKind
			} = errorResult;

			switch (authenticationKind) {
				case IntegrationFlowAuthenticationKind.OAuth2:
					switch (errorKind) {
						case IntegrationFlowAuthenticationErrorKind.AuthenticationIsRequired:
							const oauth2Authentication: TryResult<void> = await openOAuth2AuthenticationRequiredDialog({
								redirectUrl: errorResult
									.redirectUrl
							});

							return oauth2Authentication.wasSuccessful;
						default:
							return never(errorKind);
					}
				case IntegrationFlowAuthenticationKind.Afas:
					switch (errorKind) {
						case IntegrationFlowAuthenticationErrorKind.AuthenticationIsRequired:
							afasOtpHandler
								.reducer
								.setValue({
									isSet: true,
									value: ""
								});

							afasOtpHandler
								.validation
								.triggerInputFinished();
							return false;
						case IntegrationFlowAuthenticationErrorKind.EmailAddressIsRequired:
							const { isAdmin } = userConfiguration();

							alertManager.showError(
								localize(isAdmin
									? "_AfasEmailRequiredToActivateMessageForAdmin"
									: "_AfasEmailRequiredToActivateMessage"
								)
							);
							return false;
						case IntegrationFlowAuthenticationErrorKind.IntegrationError:
							alertManager.showError(localize("_UnableToValidateConnection"));
							return false;
						default:
							return never(errorKind);
					}
				default:
					never(authenticationKind);
			}
		},
		[
			afasOtpHandler,
			alertManager,
			openOAuth2AuthenticationRequiredDialog
		]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { value, validation } = formHandler;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const model: IntegrationFlowAuthenticationSaveModel = prepareForSaving(value);

				if (isNotNil(id)) {
					await updateAuthentication(
						id,
						model,
						globalCancellation
					);

					onAfterSave({
						id,
						kind: model.kind,
						name: model.name
					});
				} else {
					const savedId: number = await createAuthentication(model, globalCancellation);
					onAfterSave({
						id: savedId,
						kind: model.kind,
						name: model.name
					});
				}

				alertManager.showSuccess(localize("_SuccessfullySavedTheAuthentication"));
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let isHandled: boolean = validation.tryProcessServerError(e, serverValidationPathRedirects);

				if (!isHandled) {
					const error: TryResult<IErrorSaveIntegrationFlowAuthenticationResult> = tryParseErrorResult<IErrorSaveIntegrationFlowAuthenticationResult>(e);

					if (error.wasSuccessful) {
						isHandled = true;

						const wasCompleted: boolean = await authenticationErrorHandler(error.result);

						if (wasCompleted) {
							submitHandler();
						}
					}
				}

				if (!isHandled) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formHandler,
			globalCancellation,
			id,
			isBusy,
			onAfterSave,
			authenticationErrorHandler
		]
	);

	const cancelAfasOtpAuthHandler = (): void => {
		afasOtpHandler
			.reducer
			.setValue({ isSet: false });

		afasOtpHandler
			.validation
			.triggerInputFinished();
	};

	const isNew: boolean = isNil(id);
	const isAfasOtpDialogOpen: boolean = formData
		.afas
		.oneTimePassword
		.isSet;

	return (
		<>
			<Dialog
				trackingId="integration-builder-authentication-dialog"
				variant="left-slider"
				onClose={onClose}
				hasBusyIndicator={
					!isAfasOtpDialogOpen
						? isBusy
						: undefined
				}
			>
				<Dialog.Header
					title={localize(
						isNew
							? "_NewAuthentication"
							: "_EditAuthentication"
					)}
					onClose={onClose}
				/>

				<Dialog.Body>
					<Form
						onSubmit={submitHandler}
						hasWidthConstrained
					>
						<FormItem label={localize("_Name")} isRequired>
							<TextInput
								{...formHandler.for("name").attachToTextInput()}
								autoFocus={isNew ? "focus" : undefined}
							/>
							<ValidationError {...formHandler.for("name").attachToValidationError()} />
						</FormItem>

						<FormItem label={localize("_Type")}>
							<Select
								{...formHandler.for("kind").attachToSelect()}
								options={kindOptions}
								menuTitle={localize("_Type")}
								isDisabled={!isNew}
							/>

							<ValidationError {...formHandler.for("kind").attachToValidationError()} />
						</FormItem>

						<AuthenticationSettings
							attachable={formHandler}
							isNew={isNew}
							oAuth2RedirectUrl={oAuth2RedirectUrl}
						/>

						<RestrictionSettings
							onToggle={allowedHostsToggledHandler}
							attachable={formHandler.for("allowedHosts")}
						/>
					</Form>
				</Dialog.Body>

				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_Save")}
							trackingId="save"
							onClick={submitHandler}
						/>

						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							trackingId="cancel"
							onClick={onClose}
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{afasOtpHandler.whenType(
				x => x.isSet,
				otpHandler => (
					<AfasOtpDialog
						attachable={otpHandler.for("value")}
						isBusy={isBusy}
						onActivate={() => submitHandler()}
						onClose={cancelAfasOtpAuthHandler}
					/>
				)
			)}
			{oAuth2AuthenticationRequiredDialog}
			{confirmationDialogElement}
		</>
	);
};
AuthenticationDialog.displayName = "AuthenticationDialog";

const validationConfiguration: IValidationConfiguration<AuthenticationFormData> = createValidationConfiguration<AuthenticationFormData>();

validationConfiguration.forScalar("name")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const allowedHostsCollectionConfiguration: ICollectionValidatorBuilder<HostFormModel, readonly HostFormModel[]> = validationConfiguration
	.forObject("allowedHosts")
	.when(x => x.isSet)
	.forCollection("value");

allowedHostsCollectionConfiguration
	.add(
		hasMinimumEntries(MIN_HOST_COUNT),
		hasMaximumEntries(MAX_HOST_COUNT)
	);

allowedHostsCollectionConfiguration
	.forItems(getClientId)
	.forScalar("value")
	.add(
		isNotEmpty(),
		doesNotExceedLength(261)
	);

validationConfiguration
	.when((x) => x.kind === IntegrationFlowAuthenticationKind.ApiKey)
	.forObject("apiKey")
	.addValidators(apiKeyValidationConfiguration);

validationConfiguration
	.when((x) => x.kind === IntegrationFlowAuthenticationKind.ClientCertificate)
	.forObject("clientCertificate")
	.addValidators(clientCertificateValidationConfiguration);

validationConfiguration
	.when((x) => x.kind === IntegrationFlowAuthenticationKind.OAuth2)
	.forObject("oAuth2")
	.addValidators(oAuth2ValidationConfiguration);

validationConfiguration
	.when(x => x.kind === IntegrationFlowAuthenticationKind.Afas)
	.forObject("afas")
	.addValidators(afasValidationConfiguration);

export class AllowedHostsPathReplacementPattern implements ReplacementPattern {
	public tryReplace(path: string, replacement: string): TryResult<string> {
		const regex: RegExp = /^AllowedHosts\.Value\[(\d+)\]$/;
		const match: RegExpMatchArray | null = path.match(regex);

		if (!match) {
			return { wasSuccessful: false };
		}

		const newPath: string = replacement.replace(/\{0\}/g, match[1]);

		return {
			wasSuccessful: true,
			result: newPath
		};
	}
}

const serverValidationPathRedirects: readonly PathRedirect[] = [
	{
		pattern: new AllowedHostsPathReplacementPattern(),
		replacement: "AllowedHosts.Value[{0}].Value"
	}
];

function getInitialAllowedHosts(): OptionalFormDataModel<readonly HostFormModel[]> {
	return {
		isSet: true,
		value: [
			setClientId({ value: "" })
		]
	};
}

function getInitialFormData(): AuthenticationFormData {
	return {
		name: "",
		kind: IntegrationFlowAuthenticationKind.ApiKey,
		apiKey: defaultApiKeySettings,
		oAuth2: defaultOAuth2Settings,
		clientCertificate: defaultClientCertificateSettings,
		afas: defaultAfasSettings,
		allowedHosts: getInitialAllowedHosts()
	};
};

const kindOptions: readonly SelectItem<IntegrationFlowAuthenticationKind>[] = [
	{ value: IntegrationFlowAuthenticationKind.ApiKey, text: localize("_ApiKey") },
	{ value: IntegrationFlowAuthenticationKind.OAuth2, text: localize("_OAuth2") },
	{
		value: IntegrationFlowAuthenticationKind.Basic,
		text: resolveFormattedTextString({
			template: localize("_TextWithClarificationFormat"),
			replacements: [
				{
					what: "{{Text}}",
					filler: localize("_BasicAuth")
				},
				{
					what: "{{Clarification}}",
					filler: localize("_IntegrationCredentials")
						.toLowerCase()
				}
			]
		})
	},
	{ value: IntegrationFlowAuthenticationKind.ClientCertificate, text: localize("_ClientCertificate") },
	{
		value: IntegrationFlowAuthenticationKind.Afas,
		text: localize("_AfasAuthentication"),
		groupName: "custom"
	}
];

function convertToFormData(model: IntegrationFlowAuthenticationViewModel): AuthenticationFormData {
	const {
		kind,
		name,
		allowedHosts: allowedHostsViewModel
	} = model;

	const allowedHosts: OptionalFormDataModel<readonly HostFormModel[]> = allowedHostsViewModel
		.isSet
			? {
				isSet: true,
				value: allowedHostsViewModel
					.value
					.map(
						x => setClientId({
							value: x
						})
					)
			}
			: {
				...getInitialAllowedHosts(),
				isSet: false
			};

	switch (kind) {
		case IntegrationFlowAuthenticationKind.ApiKey:
			return {
				name,
				kind: IntegrationFlowAuthenticationKind.ApiKey,
				apiKey: {
					headers: model.headers
						.map((x) => setClientId(
							convertKeyedValue(x)
						)),
					queryParameters: model.queryParameters
						.map((x) => setClientId(
							convertKeyedValue(x)
						))
				},
				oAuth2: defaultOAuth2Settings,
				clientCertificate: defaultClientCertificateSettings,
				afas: defaultAfasSettings,
				allowedHosts
			};
		case IntegrationFlowAuthenticationKind.OAuth2:
			const {
				tokenUrl,
				authorizationUrl,
				clientId,
				scopes
			} = model;

			return {
				name,
				kind: IntegrationFlowAuthenticationKind.OAuth2,
				oAuth2: {
					tokenUrl: tokenUrl ?? "",
					authorizationUrl: authorizationUrl ?? "",
					clientId: clientId ?? "",
					clientSecret: {
						containsNewValue: false,
						value: null
					},
					scopes
				},
				apiKey: defaultApiKeySettings,
				clientCertificate: defaultClientCertificateSettings,
				afas: defaultAfasSettings,
				allowedHosts
			};
		case IntegrationFlowAuthenticationKind.Basic:
			return {
				name,
				kind: IntegrationFlowAuthenticationKind.Basic,
				apiKey: defaultApiKeySettings,
				oAuth2: defaultOAuth2Settings,
				clientCertificate: defaultClientCertificateSettings,
				afas: defaultAfasSettings,
				allowedHosts
			};
		case IntegrationFlowAuthenticationKind.ClientCertificate:
			const { certificateFileName } = model;

			return {
				name,
				kind: IntegrationFlowAuthenticationKind.ClientCertificate,
				apiKey: defaultApiKeySettings,
				oAuth2: defaultOAuth2Settings,
				clientCertificate: {
					initialFileName: certificateFileName,
					certificate: { isSet: false },
					password: {
						containsNewValue: false,
						value: null
					}
				},
				afas: defaultAfasSettings,
				allowedHosts
			};
		case IntegrationFlowAuthenticationKind.Afas:
			const {
				apiUrl,
				environmentKey
			} = model;

			return {
				name,
				kind: IntegrationFlowAuthenticationKind.Afas,
				apiKey: defaultApiKeySettings,
				oAuth2: defaultOAuth2Settings,
				clientCertificate: defaultClientCertificateSettings,
				afas: {
					apiUrl,
					environmentKey,
					apiKey: {
						containsNewValue: false,
						value: null
					},
					oneTimePassword: { isSet: false }
				},
				allowedHosts
			};
		default:
			never(model);
	}
}

function convertKeyedValue(model: KeyedValueViewModel): KeyedValueSaveModel {
	return {
		id: model.id,
		key: model.key,
		value: convertStringOrSecret(model.value)
	};
}

function convertStringOrSecret(model: StringOrSecretViewModel): StringOrSecretSaveModel {
	switch (model.kind) {
		case StringSecretDiscriminator.String:
			return {
				kind: StringSecretDiscriminator.String,
				string: model.string
			};
		case StringSecretDiscriminator.Secret:
			return {
				kind: StringSecretDiscriminator.Secret,
				secret: {
					containsNewValue: false,
					value: null
				}
			};
		default:
			never(model);
	}
}

function prepareForSaving(
	value: AuthenticationFormData,
): IntegrationFlowAuthenticationSaveModel {
	const {
		kind,
		name,
		apiKey,
		oAuth2,
		clientCertificate,
		afas
	} = value;

	const allowedHosts: OptionalModel<readonly string[]> = value.allowedHosts.isSet
		? {
			isSet: true,
			value: value
				.allowedHosts
				.value
				.map(x => x.value)
		}
		: { isSet: false };

	switch (kind) {
		case IntegrationFlowAuthenticationKind.ApiKey:
			return {
				name,
				kind: IntegrationFlowAuthenticationKind.ApiKey,
				apiKey,
				allowedHosts
			};
		case IntegrationFlowAuthenticationKind.OAuth2:
			return {
				name,
				kind: IntegrationFlowAuthenticationKind.OAuth2,
				oAuth2,
				allowedHosts
			};
		case IntegrationFlowAuthenticationKind.Basic:
			return {
				name,
				kind: IntegrationFlowAuthenticationKind.Basic,
				allowedHosts
			};
		case IntegrationFlowAuthenticationKind.ClientCertificate:
			return {
				name,
				kind: IntegrationFlowAuthenticationKind.ClientCertificate,
				allowedHosts,
				clientCertificate: {
					...clientCertificate,
					certificate: prepareCertificateForSaving(clientCertificate.certificate)
				}
			};
		case IntegrationFlowAuthenticationKind.Afas:
			return {
				name,
				kind: IntegrationFlowAuthenticationKind.Afas,
				afas,
				allowedHosts
			};
		default:
			never(kind);
	}
}

function prepareCertificateForSaving(certificate: OptionalModel<Base64FileUriUploadWithMetadataModel | null>): OptionalModel<Base64FileUriUploadModel> {
	if (!certificate.isSet) {
		return { isSet: false };
	}

	const {
		filename,
		uri
	} = ensureNotNil(certificate.value);

	return {
		isSet: true,
		value: {
			filename,
			uri
		}
	};
};

function isOAuth2Model(model: IntegrationFlowAuthenticationViewModel): model is IntegrationFlowOAuth2AuthenticationViewModel {
	return model.kind === IntegrationFlowAuthenticationKind.OAuth2;
}

export type AuthenticationDialogInitialData = Omit<
	AuthenticationDialogProps,
	"onAfterSave" | "onClose"
>;

export function useAuthenticationDialog(): [
	element: ReactElement | null,
	open: (props: AuthenticationDialogInitialData) => Promise<TryResult<IntegrationFlowAuthenticationOptionModel>>
] {
	return useDataInputDialog(
		(props, onClose, onAfterSave) => (
			<AuthenticationDialog
				{...props}
				onAfterSave={onAfterSave}
				onClose={onClose}
			/>
		)
	);
}
